import { defineQuiz, type Answers } from "@/schema/quizSchema";

const ORDER = ["q1", "q2", "q3", "q4", "q5"] as const;

export const placeholderQuiz = defineQuiz({
  id: "placeholder",
  slug: "placeholder",
  tenant: "default",
  meta: {
    title: "What kind of maker are you?",
    introHeadline: "A 60-second placeholder quiz",
    introBody: "Generic content that proves the engine runs end-to-end.",
  },
  questions: [
    {
      id: "q1",
      type: "single",
      prompt: "When you start something new, what pulls you in first?",
      options: [
        { value: "discover", label: "Discovering how it works" },
        { value: "make", label: "Building the thing" },
        { value: "share", label: "Getting others involved" },
      ],
    },
    {
      id: "q2",
      type: "multi",
      prompt: "Which of these energize you? (pick any)",
      min: 0,
      options: [
        { value: "learn", label: "Learning something new" },
        { value: "build", label: "Making it work" },
        { value: "ship", label: "Shipping it" },
        { value: "teach", label: "Explaining it to others" },
      ],
    },
    {
      id: "q3",
      type: "slider",
      prompt: "How much do you plan before you act?",
      min: 0,
      max: 10,
      minLabel: "Just start",
      maxLabel: "Plan it all",
    },
    {
      id: "q4",
      type: "info",
      heading: "Almost there",
      body: ["We're matching your answers to a profile."],
    },
    {
      id: "q5",
      type: "single",
      prompt: "You do your best work...",
      options: [
        { value: "alone", label: "On my own" },
        { value: "team", label: "With a team" },
      ],
    },
  ],

  personas: [
    {
      key: "explorer",
      label: "The Explorer",
      result: {
        tagline: "You chase the new.",
        body: [
          "You're energized by discovery and unfamiliar territory.",
          "Curiosity is your default setting.",
        ],
      },
    },
    {
      key: "builder",
      label: "The Builder",
      result: {
        tagline: "You make things real.",
        body: ["You turn ideas into working things.", "Momentum matters more than perfection."],
      },
    },
    {
      key: "connector",
      label: "The Connector",
      result: {
        tagline: "You bring people along.",
        body: ["You create value by linking people and ideas.", "Sharing is how you think."],
      },
    },
  ],

  branching(_answers: Answers, currentQuestionId: string): string | null {
    const idx = ORDER.indexOf(currentQuestionId as (typeof ORDER)[number]);
    if (idx === -1) return null;
    return ORDER[idx + 1] ?? null;
  },

  classifier(answers: Answers): string {
    const score: Record<string, number> = { explorer: 0, builder: 0, connector: 0 };

    if (answers.q1 === "discover") score.explorer += 2;
    if (answers.q1 === "make") score.builder += 2;
    if (answers.q1 === "share") score.connector += 2;

    const picks = Array.isArray(answers.q2) ? answers.q2 : [];
    if (picks.includes("learn")) score.explorer += 1;
    if (picks.includes("build") || picks.includes("ship")) score.builder += 1;
    if (picks.includes("teach")) score.connector += 1;

    const plan = typeof answers.q3 === "number" ? answers.q3 : 5;
    if (plan >= 7) score.builder += 1;
    else if (plan <= 3) score.explorer += 1;

    if (answers.q5 === "team") score.connector += 1;

    // Deterministic argmax with stable tie-break order.
    const order = ["explorer", "builder", "connector"] as const;
    return order.reduce<string>((best, k) => (score[k] > score[best] ? k : best), order[0]);
  },
});
